/**
 * Chrome preview via `?csk=paywall|regwall|share|gift|suspense`.
 * `?preview=` is an alias (`preview` is reserved by Next.js Preview Mode and
 * can be stripped from the URL on hydrate). `suspense` is handled in
 * ArticleStorySuspense; the rest in CskPopupPreview.
 */
export const CSK_PREVIEW_QUERY = "csk";

export const CSK_PREVIEW_QUERY_ALIASES = ["csk", "preview"] as const;

export const CSK_PREVIEW_MODES = [
  "paywall",
  "regwall",
  "share",
  "gift",
  "suspense",
] as const;

export type CskPreviewMode = (typeof CSK_PREVIEW_MODES)[number];

const MODE_SET = new Set<string>(CSK_PREVIEW_MODES);

function firstQueryValue(
  value: string | string[] | undefined,
): string | undefined {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
}

export function parseCskPreviewMode(
  raw: string | string[] | undefined,
): CskPreviewMode | null {
  const value = firstQueryValue(raw);
  if (!value || !MODE_SET.has(value)) {
    return null;
  }
  return value as CskPreviewMode;
}

export function readCskPreviewFromSearch(
  search: string,
): CskPreviewMode | null {
  const params = new URLSearchParams(search);
  for (const key of CSK_PREVIEW_QUERY_ALIASES) {
    const mode = parseCskPreviewMode(params.get(key) ?? undefined);
    if (mode) {
      return mode;
    }
  }
  return null;
}

export function readCskPreviewFromLocation(): CskPreviewMode | null {
  if (typeof window === "undefined") {
    return null;
  }
  return readCskPreviewFromSearch(window.location.search);
}

export function readCskPreviewFromQuery(
  query: Record<string, string | string[] | undefined>,
): CskPreviewMode | null {
  for (const key of CSK_PREVIEW_QUERY_ALIASES) {
    const mode = parseCskPreviewMode(query[key]);
    if (mode) {
      return mode;
    }
  }
  return null;
}
